<template>
  <!-- =========================================================
       FLOATING ACCESSIBILITY LAUNCHER
  ========================================================== -->
  <button
    type="button"
    class="accessibility-launcher"
    :class="{
      'accessibility-launcher--expanded': hovered
    }"
    :style="launcherStyle"
    :aria-label="
      isFrench
        ? 'Ouvrir les outils d’accessibilité'
        : 'Open Accessibility Tools'
    "
    :aria-expanded="accessibilityVisible"
    @mouseenter="expand"
    @mouseleave="collapse"
    @click="openAccessibility"
  >
    <!-- =====================================================
         TEXT

         Exactly like AI launcher:
         hidden while collapsed,
         revealed when expanded.
    ====================================================== -->
    <span
      class="accessibility-launcher__label"
      :style="sysTypo.sectionTypography.button"
    >
      {{
        isFrench
          ? "Accessibilité"
          : "Accessibility"
      }}
    </span>

    <!-- =====================================================
         ICON

         Stays fixed on the RIGHT.
    ====================================================== -->
    <span class="accessibility-launcher__icon">
      <v-icon size="28">
        mdi-human
      </v-icon>
    </span>
  </button>


  <!-- =========================================================
       ACCESSIBILITY PANEL
  ========================================================== -->
  <transition name="accessibility-panel">
    <div
      v-if="accessibilityVisible"
      class="accessibility-panel"
      :style="panelStyle"
      role="dialog"
      aria-modal="false"
      aria-labelledby="accessibility-title"
    >
      <!-- =====================================================
           HEADER
      ====================================================== -->
      <div
        class="accessibility-panel__header"
        :style="headerStyle"
      >
        <div class="accessibility-panel__header-content">
          <span class="accessibility-panel__header-icon">
            <v-icon size="25">
              mdi-human
            </v-icon>
          </span>

          <div>
            <h2
              id="accessibility-title"
              class="accessibility-panel__title"
              :style="sysTypo.sectionTypography.h2"
            >
              {{
                isFrench
                  ? "Accessibilité"
                  : "Accessibility"
              }}
            </h2>

            <p
              class="accessibility-panel__subtitle"
              :style="sysTypo.sectionTypography.small"
            >
              {{
                isFrench
                  ? "Adaptez le site Web à vos besoins"
                  : "Adjust the website to your needs"
              }}
            </p>
          </div>
        </div>

        <button
          type="button"
          class="accessibility-panel__close"
          :aria-label="
            isFrench
              ? 'Fermer'
              : 'Close'
          "
          @click="closeAccessibility"
        >
          <v-icon size="23">
            mdi-close
          </v-icon>
        </button>
      </div>


      <!-- =====================================================
           BODY
      ====================================================== -->
      <div
        class="accessibility-panel__body"
        :style="bodyStyle"
      >
        <!-- ===================================================
             TEXT SIZE
        ==================================================== -->
        <section class="accessibility-section">
          <div
            class="accessibility-section__title"
            :style="sysTypo.sectionTypography.label"
          >
            <v-icon size="20">
              mdi-format-size
            </v-icon>

            <span>
              {{
                isFrench
                  ? "Taille du texte"
                  : "Text size"
              }}
            </span>
          </div>

          <div class="text-size-controls">
            <button
              type="button"
              class="text-size-button"
              :style="smallButtonStyle"
              :disabled="settings.textScale <= 0.8"
              aria-label="Decrease text size"
              @click="decreaseTextSize"
            >
              <v-icon size="20">
                mdi-minus
              </v-icon>
            </button>

            <div
              class="text-size-value"
              :style="sysTypo.sectionTypography.body"
            >
              {{
                Math.round(
                  settings.textScale * 100
                )
              }}%
            </div>

            <button
              type="button"
              class="text-size-button"
              :style="smallButtonStyle"
              :disabled="settings.textScale >= 1.5"
              aria-label="Increase text size"
              @click="increaseTextSize"
            >
              <v-icon size="20">
                mdi-plus
              </v-icon>
            </button>
          </div>
        </section>


        <div class="accessibility-divider"></div>


        <!-- ===================================================
             DISPLAY
        ==================================================== -->
        <section class="accessibility-section">
          <div
            class="accessibility-section__title"
            :style="sysTypo.sectionTypography.label"
          >
            {{
              isFrench
                ? "Affichage"
                : "Display"
            }}
          </div>


          <!-- =================================================
               SQUARE BLOCKS
          ================================================== -->
          <div class="accessibility-grid">

            <!-- HIGH CONTRAST -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.highContrast
              }"
              :style="
                optionStyle(
                  settings.highContrast
                )
              "
              :aria-pressed="
                settings.highContrast
              "
              @click="
                toggleSetting(
                  'highContrast'
                )
              "
            >
              <v-icon size="28">
                mdi-contrast-circle
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Contraste élevé"
                    : "High Contrast"
                }}
              </span>

              <v-icon
                v-if="settings.highContrast"
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>


            <!-- GRAYSCALE -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.grayscale
              }"
              :style="
                optionStyle(
                  settings.grayscale
                )
              "
              :aria-pressed="
                settings.grayscale
              "
              @click="
                toggleSetting(
                  'grayscale'
                )
              "
            >
              <v-icon size="28">
                mdi-invert-colors
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Niveaux de gris"
                    : "Grayscale"
                }}
              </span>

              <v-icon
                v-if="settings.grayscale"
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>


            <!-- UNDERLINE LINKS -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.underlineLinks
              }"
              :style="
                optionStyle(
                  settings.underlineLinks
                )
              "
              :aria-pressed="
                settings.underlineLinks
              "
              @click="
                toggleSetting(
                  'underlineLinks'
                )
              "
            >
              <v-icon size="28">
                mdi-link-variant
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Souligner les liens"
                    : "Underline Links"
                }}
              </span>

              <v-icon
                v-if="
                  settings.underlineLinks
                "
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>


            <!-- READABLE FONT -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.readableFont
              }"
              :style="
                optionStyle(
                  settings.readableFont
                )
              "
              :aria-pressed="
                settings.readableFont
              "
              @click="
                toggleSetting(
                  'readableFont'
                )
              "
            >
              <v-icon size="28">
                mdi-format-font
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Police lisible"
                    : "Readable Font"
                }}
              </span>

              <v-icon
                v-if="
                  settings.readableFont
                "
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>


            <!-- TEXT SPACING -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.textSpacing
              }"
              :style="
                optionStyle(
                  settings.textSpacing
                )
              "
              :aria-pressed="
                settings.textSpacing
              "
              @click="
                toggleSetting(
                  'textSpacing'
                )
              "
            >
              <v-icon size="28">
                mdi-format-letter-spacing
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Espacement du texte"
                    : "Text Spacing"
                }}
              </span>

              <v-icon
                v-if="
                  settings.textSpacing
                "
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>


            <!-- REDUCE MOTION -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.reduceMotion
              }"
              :style="
                optionStyle(
                  settings.reduceMotion
                )
              "
              :aria-pressed="
                settings.reduceMotion
              "
              @click="
                toggleSetting(
                  'reduceMotion'
                )
              "
            >
              <v-icon size="28">
                mdi-motion-pause-outline
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Réduire les animations"
                    : "Reduce Motion"
                }}
              </span>

              <v-icon
                v-if="
                  settings.reduceMotion
                "
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>


            <!-- LARGE CURSOR -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.largeCursor
              }"
              :style="
                optionStyle(
                  settings.largeCursor
                )
              "
              :aria-pressed="
                settings.largeCursor
              "
              @click="
                toggleSetting(
                  'largeCursor'
                )
              "
            >
              <v-icon size="28">
                mdi-cursor-default-outline
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Grand curseur"
                    : "Large Cursor"
                }}
              </span>

              <v-icon
                v-if="
                  settings.largeCursor
                "
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>


            <!-- FOCUS HIGHLIGHT -->
            <button
              type="button"
              class="accessibility-option"
              :class="{
                'accessibility-option--active':
                  settings.focusHighlight
              }"
              :style="
                optionStyle(
                  settings.focusHighlight
                )
              "
              :aria-pressed="
                settings.focusHighlight
              "
              @click="
                toggleSetting(
                  'focusHighlight'
                )
              "
            >
              <v-icon size="28">
                mdi-target
              </v-icon>

              <span
                :style="
                  sysTypo.sectionTypography.button
                "
              >
                {{
                  isFrench
                    ? "Mise en évidence"
                    : "Focus Highlight"
                }}
              </span>

              <v-icon
                v-if="
                  settings.focusHighlight
                "
                class="option-check"
                size="17"
              >
                mdi-check-circle
              </v-icon>
            </button>

          </div>
        </section>


        <!-- ===================================================
             RESET
        ==================================================== -->
        <div class="accessibility-reset">
          <v-btn
            block
            variant="outlined"
            prepend-icon="mdi-restore"
            :style="resetButtonStyle"
            @click="resetAccessibility"
          >
            {{
              isFrench
                ? "Réinitialiser les paramètres"
                : "Reset accessibility settings"
            }}
          </v-btn>
        </div>
      </div>
    </div>
  </transition>
</template>


<script setup>
import {
  computed,
  onBeforeUnmount,
  onMounted,
  reactive,
  ref,
  watch
} from "vue"

import {
  useI18n
} from "vue-i18n"

import {
  useThemeStore
} from "@/store/auth"

import {
  useSysTypo
} from "@/store/useSysTypo"


// =============================================================
// STORES
// =============================================================

const themeStore =
  useThemeStore()

const sysTypo =
  useSysTypo()

const {
  locale
} = useI18n()


// =============================================================
// LANGUAGE
// =============================================================

const isFrench =
  computed(() => {

    return String(
      locale.value || "en"
    )
      .toLowerCase()
      .startsWith("fr")

  })


// =============================================================
// STORAGE
// =============================================================

const STORAGE_KEY =
  "nexgbits-accessibility-settings"


// =============================================================
// STATE
// =============================================================

const hovered =
  ref(false)

const accessibilityVisible =
  ref(false)


const defaultSettings = {
  textScale: 1,

  highContrast: false,

  grayscale: false,

  underlineLinks: false,

  readableFont: false,

  textSpacing: false,

  reduceMotion: false,

  largeCursor: false,

  focusHighlight: false
}


const settings =
  reactive({
    ...defaultSettings
  })


// =============================================================
// THEME
// =============================================================

const primary =
  computed(() =>
    themeStore.sectionTheme.primary ||
    themeStore.sectionTheme.btnBgColor ||
    "#1f6f63"
  )


const buttonBackground =
  computed(() =>
    themeStore.sectionTheme.btnBgColor ||
    themeStore.sectionTheme.primary ||
    "#1f6f63"
  )


const buttonColor =
  computed(() =>
    themeStore.sectionTheme.btnColor ||
    "#ffffff"
  )


const cardBackground =
  computed(() =>
    themeStore.sectionTheme.cardBodyBg ||
    themeStore.sectionTheme.sectionBgColor ||
    "#ffffff"
  )


const cardColor =
  computed(() =>
    themeStore.sectionTheme.cardBodyColor ||
    themeStore.sectionTheme.sectionTextColor ||
    "#1f2937"
  )


const alternateBackground =
  computed(() =>
    themeStore.sectionTheme.cardAlternateBgColor ||
    themeStore.sectionTheme.sectionAlternateBgColor ||
    "#f5f7fa"
  )


// =============================================================
// INLINE STYLES
// =============================================================

const launcherStyle =
  computed(() => ({

    ...sysTypo.sectionTypography.button,

    background:
      buttonBackground.value,

    color:
      buttonColor.value

  }))


const panelStyle =
  computed(() => ({

    background:
      cardBackground.value,

    color:
      cardColor.value,

    borderColor:
      primary.value

  }))


const headerStyle =
  computed(() => ({

    background:
      themeStore.sectionTheme.cardTitleBgColor ||
      primary.value,

    color:
      themeStore.sectionTheme.cardTitleTextColor ||
      buttonColor.value

  }))


const bodyStyle =
  computed(() => ({

    background:
      cardBackground.value,

    color:
      cardColor.value

  }))


const smallButtonStyle =
  computed(() => ({

    ...sysTypo.sectionTypography.button,

    background:
      alternateBackground.value,

    color:
      primary.value,

    borderColor:
      primary.value

  }))


const resetButtonStyle =
  computed(() => ({

    ...sysTypo.sectionTypography.button,

    color:
      primary.value,

    borderColor:
      primary.value

  }))


function optionStyle(
  active
) {

  if (active) {

    return {

      ...sysTypo.sectionTypography.button,

      background:
        buttonBackground.value,

      color:
        buttonColor.value,

      borderColor:
        buttonBackground.value

    }

  }


  return {

    ...sysTypo.sectionTypography.button,

    background:
      alternateBackground.value,

    color:
      cardColor.value,

    borderColor:
      primary.value

  }

}


// =============================================================
// LAUNCHER
//
// SAME BEHAVIOR AS AI LAUNCHER
// =============================================================

function expand() {

  hovered.value = true

}


function collapse() {

  hovered.value = false

}


function openAccessibility() {

  /*
   * Exactly like the AI launcher:
   * collapse immediately when clicked.
   */
  hovered.value = false


  /*
   * Toggle panel.
   */
  accessibilityVisible.value =
    !accessibilityVisible.value

}


function closeAccessibility() {

  hovered.value = false

  accessibilityVisible.value = false

}


// =============================================================
// TEXT SIZE
// =============================================================

function increaseTextSize() {

  if (
    settings.textScale >= 1.5
  ) {
    return
  }


  settings.textScale =
    Number(
      Math.min(
        1.5,
        settings.textScale + 0.1
      ).toFixed(1)
    )

}


function decreaseTextSize() {

  if (
    settings.textScale <= 0.8
  ) {
    return
  }


  settings.textScale =
    Number(
      Math.max(
        0.8,
        settings.textScale - 0.1
      ).toFixed(1)
    )

}


// =============================================================
// TOGGLE
// =============================================================

function toggleSetting(
  key
) {

  if (
    Object.prototype.hasOwnProperty.call(
      settings,
      key
    )
  ) {

    settings[key] =
      !settings[key]

  }

}


// =============================================================
// APPLY SETTINGS
// =============================================================

function applyAccessibilitySettings() {

  const root =
    document.documentElement


  const body =
    document.body


  root.style.setProperty(
    "--accessibility-text-scale",
    String(
      settings.textScale
    )
  )


  root.classList.toggle(
    "a11y-text-scale",
    settings.textScale !== 1
  )


  root.classList.toggle(
    "a11y-high-contrast",
    settings.highContrast
  )


  root.classList.toggle(
    "a11y-grayscale",
    settings.grayscale
  )


  root.classList.toggle(
    "a11y-underline-links",
    settings.underlineLinks
  )


  root.classList.toggle(
    "a11y-readable-font",
    settings.readableFont
  )


  root.classList.toggle(
    "a11y-text-spacing",
    settings.textSpacing
  )


  root.classList.toggle(
    "a11y-reduce-motion",
    settings.reduceMotion
  )


  root.classList.toggle(
    "a11y-large-cursor",
    settings.largeCursor
  )


  root.classList.toggle(
    "a11y-focus-highlight",
    settings.focusHighlight
  )


  if (body) {

    const enabled =
      settings.textScale !== 1 ||
      settings.highContrast ||
      settings.grayscale ||
      settings.underlineLinks ||
      settings.readableFont ||
      settings.textSpacing ||
      settings.reduceMotion ||
      settings.largeCursor ||
      settings.focusHighlight


    body.setAttribute(
      "data-accessibility-enabled",
      enabled
        ? "true"
        : "false"
    )

  }

}


// =============================================================
// SAVE
// =============================================================

function saveSettings() {

  try {

    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        ...settings
      })
    )

  }
  catch (error) {

    console.warn(
      "[Accessibility] Unable to save settings:",
      error
    )

  }

}


// =============================================================
// LOAD
// =============================================================

function loadSettings() {

  try {

    const stored =
      localStorage.getItem(
        STORAGE_KEY
      )


    if (!stored) {
      return
    }


    const parsed =
      JSON.parse(
        stored
      )


    Object.keys(
      defaultSettings
    ).forEach(
      key => {

        if (
          parsed[key] !== undefined
        ) {

          settings[key] =
            parsed[key]

        }

      }
    )

  }
  catch (error) {

    console.warn(
      "[Accessibility] Unable to load settings:",
      error
    )

  }

}


// =============================================================
// RESET
// =============================================================

function resetAccessibility() {

  Object.assign(
    settings,
    defaultSettings
  )


  try {

    localStorage.removeItem(
      STORAGE_KEY
    )

  }
  catch {

    // Ignore storage errors.

  }


  applyAccessibilitySettings()

}


// =============================================================
// KEYBOARD
// =============================================================

function handleKeydown(
  event
) {

  if (
    event.key === "Escape" &&
    accessibilityVisible.value
  ) {

    closeAccessibility()

  }

}


// =============================================================
// WATCH
// =============================================================

watch(
  settings,

  () => {

    applyAccessibilitySettings()

    saveSettings()

  },

  {
    deep: true
  }
)


// =============================================================
// LIFECYCLE
// =============================================================

onMounted(() => {

  loadSettings()

  applyAccessibilitySettings()


  window.addEventListener(
    "keydown",
    handleKeydown
  )

})


onBeforeUnmount(() => {

  window.removeEventListener(
    "keydown",
    handleKeydown
  )

})
</script>


<style scoped>
/* ============================================================
   FLOATING ACCESSIBILITY LAUNCHER

   THIS IS THE SAME STRUCTURE AS YOUR AI LAUNCHER.
============================================================ */

.accessibility-launcher {
  position: fixed !important;

  right: 24px !important;

  /*
   * AI launcher is bottom:24px.
   * Accessibility sits immediately above it.
   */
  bottom: 94px !important;

  z-index: 2147483646 !important;

  width: 60px;
  min-width: 60px;
  height: 60px;

  margin: 0;
  padding: 0;

  display: flex;
  align-items: center;
  justify-content: flex-end;

  overflow: hidden;

  border:
    2px solid
    rgba(255, 255, 255, 0.95);

  border-radius: 30px;

  color: inherit;

  box-shadow:
    0 8px 25px
      rgba(0, 0, 0, 0.25),
    0 3px 9px
      rgba(0, 0, 0, 0.16);

  cursor: pointer;

  white-space: nowrap;

  /*
   * Same as AI launcher.
   *
   * right stays fixed.
   * width increases.
   * therefore expansion goes LEFT.
   */
  transform-origin:
    right center;

  transition:
    width 0.30s
      cubic-bezier(
        0.4,
        0,
        0.2,
        1
      ),
    box-shadow 0.25s ease;
}


/* ============================================================
   EXPANDED

   EXACT SAME IDEA AS:
   .ai-help-launcher--expanded
============================================================ */

.accessibility-launcher--expanded {
  width: 215px;
}


/* ============================================================
   LABEL
============================================================ */

.accessibility-launcher__label {
  display: block;

  width: 0;
  max-width: 0;

  padding: 0;

  overflow: hidden;

  opacity: 0;

  color: inherit;

  line-height: 1;

  text-align: left;

  white-space: nowrap;

  transform:
    translateX(12px);

  transition:
    width 0.30s ease,
    max-width 0.30s ease,
    padding 0.30s ease,
    opacity 0.20s ease,
    transform 0.30s ease;
}


/* ============================================================
   EXPANDED LABEL
============================================================ */

.accessibility-launcher--expanded
.accessibility-launcher__label {
  width: 153px;
  max-width: 153px;

  padding-left: 18px;

  opacity: 1;

  transform:
    translateX(0);
}


/* ============================================================
   ICON
============================================================ */

.accessibility-launcher__icon {
  width: 58px;
  min-width: 58px;
  height: 58px;

  flex:
    0 0
    58px;

  display: flex;
  align-items: center;
  justify-content: center;

  color: inherit;

  transition:
    transform 0.28s ease;
}


/* ============================================================
   ICON HOVER

   SAME AS AI LAUNCHER.
============================================================ */

.accessibility-launcher:hover
.accessibility-launcher__icon {
  transform:
    rotate(8deg);
}


/* ============================================================
   HOVER SHADOW
============================================================ */

.accessibility-launcher:hover {
  box-shadow:
    0 12px 34px
      rgba(0, 0, 0, 0.32),
    0 4px 12px
      rgba(0, 0, 0, 0.18);
}


/* ============================================================
   KEYBOARD
============================================================ */

.accessibility-launcher:focus-visible {
  outline:
    3px solid
    rgba(64, 145, 255, 0.50);

  outline-offset: 4px;
}


/* ============================================================
   ACCESSIBILITY PANEL

   Independent of launcher width.
============================================================ */

.accessibility-panel {
  position: fixed;

  right: 24px;

  bottom: 166px;

  z-index: 2147483645;

  width: 390px;

  max-width:
    calc(100vw - 32px);

  max-height:
    calc(100vh - 190px);

  overflow: hidden;

  border: 1px solid;

  border-radius: 10px;

  box-shadow:
    0 16px 45px
    rgba(0, 0, 0, 0.22);
}


/* ============================================================
   PANEL HEADER
============================================================ */

.accessibility-panel__header {
  min-height: 78px;

  display: flex;
  align-items: center;
  justify-content: space-between;

  gap: 14px;

  padding:
    14px 16px;
}


.accessibility-panel__header-content {
  min-width: 0;

  display: flex;
  align-items: center;

  gap: 12px;
}


.accessibility-panel__header-icon {
  width: 44px;
  height: 44px;

  min-width: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  border:
    1px solid
    rgba(255,255,255,0.22);

  border-radius: 7px;
}


.accessibility-panel__title {
  margin: 0;

  color: inherit !important;

  line-height: 1.2;
}


.accessibility-panel__subtitle {
  margin:
    4px 0 0;

  color: inherit !important;

  opacity: 0.85;
}


.accessibility-panel__close {
  width: 38px;
  height: 38px;

  min-width: 38px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: 0;

  border-radius: 50%;

  background:
    transparent;

  color: inherit;

  cursor: pointer;
}


/* ============================================================
   BODY
============================================================ */

.accessibility-panel__body {
  max-height:
    calc(100vh - 280px);

  padding: 18px;

  overflow-x: hidden;
  overflow-y: auto;
}


/* ============================================================
   SECTION
============================================================ */

.accessibility-section {
  width: 100%;
}


.accessibility-section__title {
  display: flex;
  align-items: center;

  gap: 7px;

  margin-bottom: 12px;

  color: inherit;
}


.accessibility-divider {
  height: 1px;

  margin:
    18px 0;

  background:
    currentColor;

  opacity: 0.12;
}


/* ============================================================
   TEXT SIZE
============================================================ */

.text-size-controls {
  display: grid;

  grid-template-columns:
    48px
    minmax(0, 1fr)
    48px;

  gap: 10px;

  align-items: center;
}


.text-size-button {
  width: 48px;
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  padding: 0;

  border: 1px solid;

  border-radius: 6px;

  cursor: pointer;
}


.text-size-button:disabled {
  opacity: 0.4;

  cursor: not-allowed;
}


.text-size-value {
  height: 44px;

  display: flex;
  align-items: center;
  justify-content: center;

  border:
    1px solid
    rgba(128,128,128,0.25);

  border-radius: 6px;

  text-align: center;
}


/* ============================================================
   2-COLUMN SQUARE ACCESSIBILITY BLOCKS
============================================================ */

.accessibility-grid {
  display: grid !important;

  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    ) !important;

  gap: 10px !important;

  width: 100%;
}


/* ============================================================
   OPTION
============================================================ */

.accessibility-option {
  position: relative;

  width: 100%;
  min-width: 0;

  min-height: 104px;

  display: flex !important;

  flex-direction: column !important;

  align-items: center !important;

  justify-content: center !important;

  gap: 8px;

  padding:
    14px 10px;

  border: 1px solid;

  border-radius: 7px;

  cursor: pointer;

  text-align: center;

  overflow: hidden;

  transition:
    box-shadow 0.18s ease,
    transform 0.18s ease;
}


.accessibility-option:hover {
  box-shadow:
    0 4px 14px
    rgba(0,0,0,0.10);

  transform:
    translateY(-1px);
}


.accessibility-option span {
  width: 100%;

  white-space: normal;

  text-align: center;
}


.option-check {
  position: absolute !important;

  top: 8px;
  right: 8px;
}


/* ============================================================
   RESET
============================================================ */

.accessibility-reset {
  margin-top: 18px;
}


/* ============================================================
   PANEL TRANSITION
============================================================ */

.accessibility-panel-enter-active,
.accessibility-panel-leave-active {
  transition:
    opacity 0.18s ease,
    transform 0.18s ease;
}


.accessibility-panel-enter-from,
.accessibility-panel-leave-to {
  opacity: 0;

  transform:
    translateY(8px)
    scale(0.985);
}


/* ============================================================
   MOBILE

   EXACT SAME PRINCIPLE AS AI LAUNCHER:
   keep circular and do not expand.
============================================================ */

@media (max-width: 768px) {

  .accessibility-launcher {
    right: 16px !important;
    bottom: 82px !important;

    width: 56px;
    min-width: 56px;
    height: 56px;

    border-radius: 28px;
  }


  .accessibility-launcher--expanded {
    width: 56px;
  }


  .accessibility-launcher__label,
  .accessibility-launcher--expanded
  .accessibility-launcher__label {
    display: none;
  }


  .accessibility-launcher__icon {
    width: 54px;
    min-width: 54px;
    height: 54px;

    flex-basis: 54px;
  }


  .accessibility-panel {
    left: 10px;
    right: 10px;

    bottom: 150px;

    width: auto;

    max-width: none;

    max-height:
      calc(100vh - 175px);
  }


  .accessibility-panel__body {
    max-height:
      calc(100vh - 275px);

    padding: 14px;
  }

}


/* ============================================================
   REDUCED MOTION
============================================================ */

@media (
  prefers-reduced-motion: reduce
) {

  .accessibility-launcher,
  .accessibility-launcher__label,
  .accessibility-launcher__icon,
  .accessibility-option,
  .accessibility-panel-enter-active,
  .accessibility-panel-leave-active {
    transition: none;
  }

}
</style>


<!-- =========================================================
     GLOBAL ACCESSIBILITY RULES

     DO NOT MAKE THIS STYLE BLOCK SCOPED.
========================================================== -->

<style>
/* ============================================================
   TEXT SCALE
============================================================ */

html.a11y-text-scale {
  font-size:
    calc(
      100% *
      var(
        --accessibility-text-scale,
        1
      )
    ) !important;
}


/* ============================================================
   HIGH CONTRAST
============================================================ */

html.a11y-high-contrast body {
  background:
    #000 !important;

  color:
    #fff !important;
}


html.a11y-high-contrast body
*:not(img):not(video):not(svg):not(path) {
  border-color:
    #fff !important;
}


html.a11y-high-contrast body a {
  color:
    #ffff00 !important;
}


/* ============================================================
   GRAYSCALE
============================================================ */

html.a11y-grayscale body {
  filter:
    grayscale(100%);
}


/* ============================================================
   UNDERLINE LINKS
============================================================ */

html.a11y-underline-links body a {
  text-decoration:
    underline !important;

  text-decoration-thickness:
    2px !important;

  text-underline-offset:
    3px !important;
}


/* ============================================================
   READABLE FONT
============================================================ */

html.a11y-readable-font body,
html.a11y-readable-font body * {
  font-family:
    Arial,
    Helvetica,
    sans-serif !important;
}


/* ============================================================
   TEXT SPACING
============================================================ */

html.a11y-text-spacing body,
html.a11y-text-spacing body p,
html.a11y-text-spacing body li,
html.a11y-text-spacing body span {
  letter-spacing:
    0.08em !important;

  word-spacing:
    0.12em !important;

  line-height:
    1.8 !important;
}


/* ============================================================
   REDUCE MOTION
============================================================ */

html.a11y-reduce-motion *,
html.a11y-reduce-motion *::before,
html.a11y-reduce-motion *::after {
  animation-duration:
    0.001ms !important;

  animation-iteration-count:
    1 !important;

  transition-duration:
    0.001ms !important;

  scroll-behavior:
    auto !important;
}


/* ============================================================
   LARGE CURSOR
============================================================ */

html.a11y-large-cursor body,
html.a11y-large-cursor body * {
  cursor:
    default !important;
}


html.a11y-large-cursor body a,
html.a11y-large-cursor body button,
html.a11y-large-cursor body [role="button"],
html.a11y-large-cursor body input,
html.a11y-large-cursor body select,
html.a11y-large-cursor body textarea {
  cursor:
    pointer !important;
}


/* ============================================================
   FOCUS HIGHLIGHT
============================================================ */

html.a11y-focus-highlight
body
*:focus-visible {
  outline:
    4px solid
    #ffbf00 !important;

  outline-offset:
    4px !important;
}
</style>